import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import HeadsUpDisplay from "@/components/HeadsUpDisplay";
import AgentActionsCenter from "@/components/AgentActionsCenter";
import PrioritiesSection from "@/components/PrioritiesSection";
import TeamLeaderboard from "@/components/TeamLeaderboard";
import UnifiedRevenueDashboard from "@/components/UnifiedRevenueDashboard";
import GoalDeltaIndicator from "@/components/GoalDeltaIndicator";
import WelcomeModal from "@/components/WelcomeModal";
import KeyboardShortcuts from "@/components/KeyboardShortcuts";
import LoadingState from "@/components/LoadingState";
import ErrorState from "@/components/ErrorState";
import GoalCompletionModal from "@/components/GoalCompletionModal";
import { useAutoSave, saveToLocalStorage } from "@/hooks/useAutoSave";

const Dashboard = () => {
  const { userId } = useParams();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [goalReached, setGoalReached] = useState(false);

  // Auto-save user data to localStorage
  useAutoSave(user, (data) => {
    saveToLocalStorage(`ism-user-${userId}`, data);
  }, 30000);

  const fetchDashboardData = useCallback(async () => {
    // Don't fetch data if userId is "new" - user hasn't completed onboarding yet
    if (userId === "new") {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      // Add cache-busting parameter to ensure fresh data
      const timestamp = Date.now();
      const [metricsRes, userRes] = await Promise.all([
        axios.get(`${API}/dashboard/${userId}?t=${timestamp}`),
        axios.get(`${API}/users/${userId}?t=${timestamp}`)
      ]);
      console.log("Dashboard data refreshed:", {
        currentRevenue: metricsRes.data.currentRevenue,
        revenueGoal: metricsRes.data.revenueGoal,
        progressPercentage: metricsRes.data.progressPercentage
      });
      setMetrics(metricsRes.data);
      setUser(userRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError(error.message || "Failed to load dashboard");
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchDashboardData();
    // Refresh data every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Check if goal is reached and show modal
  useEffect(() => {
    if (metrics && metrics.progressPercentage >= 100 && !goalReached) {
      // Add a small delay for dramatic effect
      setTimeout(() => {
        setShowGoalModal(true);
        setGoalReached(true);
      }, 1000);
    }
  }, [metrics, goalReached]);

  // Show welcome message for new users
  if (userId === "new") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Welcome to Intelligent Sales Machines!</h2>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Complete the onboarding setup to start building your sales machine and crushing your revenue goals.
          </p>
          <div className="inline-block px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-lg">
            <p className="text-sm text-teal-400">
              Click the <span className="font-semibold">"Setup"</span> button in the navigation to get started
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e17]">
        <LoadingState message="Loading command module..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0e17]">
        <ErrorState message={error} onRetry={fetchDashboardData} />
      </div>
    );
  }

  const progressPercentage = metrics?.progressPercentage || 0;
  const progressStatus = progressPercentage >= 100 ? "Goal Achieved" : 
                         progressPercentage >= 75 ? "On Pace" : 
                         progressPercentage >= 50 ? "Making Progress" : "Needs Acceleration";

  return (
    <div className="min-h-screen bg-[#0a0e17]" data-testid="dashboard">
      {/* Welcome Modal */}
      <WelcomeModal />
      
      {/* Goal Completion Modal */}
      <GoalCompletionModal 
        isOpen={showGoalModal} 
        onClose={() => setShowGoalModal(false)}
        revenueGoal={metrics?.revenueGoal || 0}
      />
      
      {/* Keyboard Shortcuts */}
      <KeyboardShortcuts />

      {/* Header */}
      <header className="border-b border-gray-800/50 bg-[#0a0e17]/95 backdrop-blur-lg sticky top-0 z-50">
        <div className="container mx-auto px-6 py-3">
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Unified Revenue Dashboard */}
        <div className="mb-8">
          <UnifiedRevenueDashboard
            goal={metrics?.revenueGoal || 0}
            current={metrics?.currentRevenue || 0}
            status={progressStatus}
            progressPercentage={progressPercentage}
            goalEndDate={user?.goalEndDate}
          />
        </div>

        {/* Three Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          {/* Left Column - Heads-Up Display */}
          <div className="lg:col-span-4">
            <HeadsUpDisplay userId={userId} onRefresh={fetchDashboardData} />
          </div>

          {/* Center Column - Agent Actions */}
          <div className="lg:col-span-4">
            <AgentActionsCenter userId={userId} onRefresh={fetchDashboardData} />
          </div>

          {/* Right Column - Priorities & Navigation */}
          <div className="lg:col-span-4">
            <PrioritiesSection userId={userId} onRefresh={fetchDashboardData} />
          </div>
        </div>

        {/* Bottom Section - Team Leaderboard */}
        <TeamLeaderboard />
      </div>

      {/* Goal-Delta Indicator */}
      <GoalDeltaIndicator userId={userId} />
    </div>
  );
};

export default Dashboard;
